<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const langkahVoice = [
  {
    step: 1,
    title: 'Buka POS Kasir',
    icon: 'ri-store-2-line',
    detail: 'Masuk ke halaman <strong>POS Kasir</strong> seperti biasa. Tombol mic muncul di sebelah kanan kolom pencarian produk.',
  },
  {
    step: 2,
    title: 'Tekan & Tahan Tombol Mic',
    icon: 'ri-mic-line',
    detail: 'Tekan dan <strong>tahan</strong> tombol mic (ikon 🎙️) — bukan cuma diklik sekali. Tombol akan berubah warna merah dan berkedip, menandakan sedang merekam.',
  },
  {
    step: 3,
    title: 'Ucapkan Pesanan dengan Jelas',
    icon: 'ri-sound-module-line',
    detail: 'Selagi tombol masih ditahan, ucapkan pesanan dengan jelas dan tidak terburu-buru. Contoh: <strong>"Americano satu"</strong> atau <strong>"dua kopi susu dan satu es teh"</strong>. Maksimal 10 detik rekaman.',
  },
  {
    step: 4,
    title: 'Lepas Tombol',
    icon: 'ri-hand-heart-line',
    detail: 'Lepas tombol mic setelah selesai bicara. Rekaman otomatis berhenti dan langsung dikirim untuk diproses — tidak ada tombol "kirim" terpisah.',
  },
  {
    step: 5,
    title: 'Tunggu Diproses',
    icon: 'ri-loader-4-line',
    detail: 'Tombol mic berubah jadi ikon loading (warna kuning) selama beberapa detik. Sistem sedang mengubah suara jadi teks, lalu mencocokkan dengan produk di katalog Anda.',
  },
  {
    step: 6,
    title: 'Cek Dialog Konfirmasi',
    icon: 'ri-file-list-3-line',
    detail: 'Dialog <strong>"Konfirmasi Pesanan Suara"</strong> otomatis terbuka, menampilkan transkrip suara Anda dan daftar produk yang terdeteksi. <strong>Selalu periksa dulu</strong> sebelum lanjut — AI bisa saja salah dengar.',
  },
  {
    step: 7,
    title: 'Koreksi Jika Perlu',
    icon: 'ri-edit-2-line',
    detail: 'Kalau ada produk yang salah/tidak terdeteksi, klik kolom produk untuk cari & ganti manual. Bisa juga ubah jumlah, hapus baris yang tidak diinginkan, atau uncheck baris yang tidak mau diproses.',
  },
  {
    step: 8,
    title: 'Tambahkan ke Keranjang',
    icon: 'ri-shopping-basket-line',
    detail: 'Klik tombol <strong>"Tambahkan N Item ke Keranjang"</strong>. Item masuk ke keranjang POS seperti biasa. Lanjutkan checkout dengan cara normal (klik produk manual, atau langsung "Bayar").',
  },
]

const contohUcapan = [
  { kalimat: 'Americano satu', hasil: '1x Americano', status: 'baik' },
  { kalimat: 'satu americano', hasil: '1x Americano', status: 'baik' },
  { kalimat: 'dua kopi susu', hasil: '2x Kopi Susu', status: 'baik' },
  { kalimat: 'dua kopi susu dan satu es teh', hasil: '2x Kopi Susu, 1x Es Teh', status: 'baik' },
  { kalimat: 'satu americano tanpa gula', hasil: '1x Americano (catatan: tanpa gula)', status: 'baik' },
  { kalimat: 'kopi susu gula aren', hasil: 'Sering tidak/salah terdeteksi', status: 'sulit' },
  { kalimat: 'saya mau yang biasa saya minum kemarin itu', hasil: 'Tidak terdeteksi — kalimat terlalu panjang/tidak spesifik', status: 'sulit' },
]

const namaProdukContoh = [
  { nama: 'Americano', ket: 'Pendek, 1 kata — paling mudah dikenali AI', baik: true },
  { nama: 'Kopi Susu', ket: '2 kata umum — mudah dikenali', baik: true },
  { nama: 'Es Teh', ket: '2 kata umum — mudah dikenali', baik: true },
  { nama: 'Kopi Susu Gula Aren', ket: '4 kata — AI harus dengar SEMUA kata persis urutannya, gampang meleset walau cuma 1 kata salah dengar', baik: false },
  { nama: 'Americano Extra Shot Less Ice Panas', ket: 'Terlalu panjang & campur istilah — sangat sulit dikenali', baik: false },
]

const faq = [
  {
    q: 'Kenapa AI kadang salah dengar / tidak menemukan produk saya?',
    a: 'Sistem mencocokkan hasil ucapan dengan NAMA VARIAN produk di katalog Anda secara keseluruhan (bukan per kata). Semakin panjang dan semakin banyak kata dalam nama produk, semakin besar kemungkinan satu kata saja salah dengar membuat seluruh pencocokan gagal. Produk dengan nama pendek dan sederhana (1-2 kata) jauh lebih akurat dibanding nama panjang seperti "Kopi Susu Gula Aren".',
  },
  {
    q: 'Bagaimana cara membuat nama produk saya lebih "AI-friendly"?',
    a: 'Cek halaman Produk & Katalog Anda — pastikan NAMA VARIAN (bukan cuma nama produk) sudah jelas dan singkat, idealnya 1-3 kata. Kalau sebuah produk cuma punya satu varian, jangan beri nama varian generik seperti "Reguler" atau "Default" saja — pastikan nama variannya sendiri sudah mengandung nama yang biasa diucapkan pelanggan (misalnya variannya bernama "Americano", bukan "Reguler").',
  },
  {
    q: 'Bisa pesan beberapa produk sekaligus dalam satu kalimat?',
    a: 'Bisa. Pisahkan tiap item dengan kata "dan", "lalu", "sama", atau koma. Contoh: "dua kopi susu dan satu es teh" akan terdeteksi sebagai 2 item terpisah.',
  },
  {
    q: 'Apakah catatan seperti "tanpa gula" atau "panas" otomatis terdeteksi?',
    a: 'Ya, untuk frasa umum seperti "tanpa gula", "tanpa es", "extra shot", "panas", "dingin", "hangat" — sistem otomatis memisahkannya jadi catatan tersendiri, tidak dianggap bagian dari nama produk yang dicari. Untuk rasa/varian khusus seperti "gula aren", ini belum termasuk catatan otomatis — sebaiknya dibuat sebagai produk/varian tersendiri.',
  },
  {
    q: 'Kenapa harus tekan-TAHAN, bukan cuma klik sekali?',
    a: 'Ini desain yang disengaja, mirip merekam voice note di WhatsApp — supaya jelas kapan mulai dan berhenti bicara, dan mencegah rekaman tidak sengaja tertinggal menyala.',
  },
  {
    q: 'Apa yang terjadi kalau saya lepas tombol lebih cepat dari saat saya selesai bicara?',
    a: 'Bagian akhir kalimat yang terpotong tidak akan ikut terekam. Sebaiknya tahan tombol sampai benar-benar selesai bicara, baru dilepas.',
  },
  {
    q: 'Apakah rekaman suara saya disimpan di server?',
    a: 'Tidak. File audio otomatis dihapus dari server segera setelah selesai diproses — baik berhasil maupun gagal. Yang tersimpan hanya teks hasil transkrip dan hasil pencocokan produk.',
  },
  {
    q: 'Apakah fitur ini butuh koneksi internet ke layanan luar (mis. OpenAI)?',
    a: 'Tidak. Semua proses (ubah suara jadi teks) berjalan di server PintuKasir Anda sendiri, sepenuhnya offline dari sisi AI pihak ketiga. Yang tetap dibutuhkan hanya koneksi kasir Anda ke server PintuKasir seperti biasa.',
  },
  {
    q: 'Bagaimana kalau fitur suara sedang bermasalah / tidak merespons?',
    a: 'POS Kasir tetap berfungsi 100% normal lewat pencarian produk manual (kolom pencarian atau klik kartu produk). Fitur suara murni lapisan tambahan, bukan satu-satunya cara input.',
  },
  {
    q: 'Kenapa microphone browser meminta izin, dan apa yang terjadi kalau saya tolak?',
    a: 'Browser wajib meminta izin akses microphone untuk keamanan pengguna. Kalau izin ditolak, tombol mic tidak akan berfungsi dan muncul pemberitahuan — tapi POS Kasir tetap bisa dipakai normal via pencarian manual.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">AI Voice Order</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn variant="text" prepend-icon="ri-arrow-left-line" size="small" class="mb-4">
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="primary" variant="tonal" size="64" rounded="lg" class="me-4">
            <VIcon icon="ri-mic-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              AI Voice Order
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Pesan produk cukup dengan bicara — tanpa mengetik atau klik satu per satu
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line" class="mb-0">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda menggunakan fitur Voice Order di POS Kasir, cara mengucapkan pesanan
          agar akurat dikenali, serta cara menamai produk supaya lebih mudah dikenali AI.
          Fitur ini bersifat <strong>opsional</strong> — kasir tetap bisa memakai pencarian
          produk manual kapan saja.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="primary" />
            Konsep Penting: Bagaimana AI Voice Order Bekerja?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Bayangkan Anda punya asisten yang mendengarkan ucapan kasir, mengubahnya jadi
              tulisan, lalu mencari sendiri produk mana di katalog Anda yang paling mirip
              dengan apa yang diucapkan. Itulah yang terjadi di balik layar setiap kali
              tombol mic ditekan:
            </p>

            <VRow dense>
              <VCol cols="12" md="4">
                <VCard color="primary" variant="tonal" class="h-100">
                  <VCardText class="text-center pa-4">
                    <VIcon icon="ri-mic-2-line" size="36" color="primary" class="mb-2" />
                    <h6 class="text-h6 font-weight-bold mb-1">1. Rekam Suara</h6>
                    <p class="text-body-2 mb-0">
                      Suara kasir direkam selama tombol mic ditahan, maksimal 10 detik.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard color="info" variant="tonal" class="h-100">
                  <VCardText class="text-center pa-4">
                    <VIcon icon="ri-file-text-line" size="36" color="info" class="mb-2" />
                    <h6 class="text-h6 font-weight-bold mb-1">2. Ubah Jadi Teks</h6>
                    <p class="text-body-2 mb-0">
                      Suara diubah menjadi teks oleh mesin AI yang berjalan di server Anda sendiri.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard color="success" variant="tonal" class="h-100">
                  <VCardText class="text-center pa-4">
                    <VIcon icon="ri-search-eye-line" size="36" color="success" class="mb-2" />
                    <h6 class="text-h6 font-weight-bold mb-1">3. Cari Produk</h6>
                    <p class="text-body-2 mb-0">
                      Teks dicocokkan dengan nama produk di katalog Anda, lalu ditampilkan untuk dikonfirmasi.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>

            <VAlert type="warning" variant="tonal" class="mt-4 mb-0">
              <strong>⚠️ Penting:</strong> AI Voice Order <strong>tidak pernah langsung checkout</strong>.
              Selalu ada dialog konfirmasi yang harus Anda periksa dan klik "Tambahkan ke Keranjang"
              secara sadar — mencegah salah pesan akibat AI salah dengar.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Pakai Step by Step -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-list-ordered" class="me-2" color="success" />
            Cara Menggunakan — Langkah demi Langkah
          </VCardTitle>
          <VCardText>
            <VTimeline
              side="end"
              align="start"
              truncate-line="both"
              density="comfortable"
            >
              <VTimelineItem
                v-for="item in langkahVoice"
                :key="item.step"
                :dot-color="item.step <= 2 ? 'warning' : item.step >= 7 ? 'info' : 'success'"
                fill-dot
                size="small"
              >
                <template #icon>
                  <span class="text-caption font-weight-bold text-white">{{ item.step }}</span>
                </template>
                <div class="d-flex align-center gap-2 mb-1">
                  <VIcon :icon="item.icon" size="18" />
                  <strong class="text-body-2">{{ item.title }}</strong>
                </div>
                <p class="text-body-2 text-medium-emphasis mb-0" v-html="item.detail" />
              </VTimelineItem>
            </VTimeline>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Test / Contoh Ucapan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-test-tube-line" class="me-2" color="primary" />
            Cara Mencoba (Testing) & Contoh Ucapan yang Akurat
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Untuk memastikan fitur ini berjalan dengan baik di toko Anda, coba urutan
              testing berikut di POS Kasir:
            </p>
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-2">Tekan &amp; tahan tombol mic, ucapkan <strong>"Americano satu"</strong>, lalu lepas.</li>
              <li class="mb-2">Tunggu dialog konfirmasi muncul — pastikan "Americano" terdeteksi dengan qty 1.</li>
              <li class="mb-2">Coba lagi dengan urutan terbalik: <strong>"satu Americano"</strong> — hasilnya harus sama.</li>
              <li class="mb-2">Coba kalimat gabungan: <strong>"dua kopi susu dan satu es teh"</strong> — harus terdeteksi jadi 2 baris terpisah.</li>
              <li class="mb-2">Coba nama produk yang lebih panjang milik Anda sendiri, dan bandingkan tingkat akurasinya.</li>
              <li class="mb-0">Kalau ada yang tidak terdeteksi, gunakan kolom pencarian di dialog konfirmasi untuk koreksi manual — ini normal dan bukan berarti fitur rusak.</li>
            </ol>

            <p class="text-body-2 mb-2 font-weight-medium">
              Contoh ucapan dan hasil yang bisa diharapkan:
            </p>
            <VTable density="compact">
              <thead>
                <tr>
                  <th>Kalimat yang Diucapkan</th>
                  <th>Hasil</th>
                  <th style="width: 120px">Tingkat Akurasi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in contohUcapan" :key="c.kalimat">
                  <td class="text-body-2">"{{ c.kalimat }}"</td>
                  <td class="text-body-2">{{ c.hasil }}</td>
                  <td>
                    <VChip
                      size="small"
                      :color="c.status === 'baik' ? 'success' : 'error'"
                      variant="tonal"
                    >
                      {{ c.status === 'baik' ? 'Akurat' : 'Sulit' }}
                    </VChip>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Nama Produk yang AI-Friendly -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-price-tag-3-line" class="me-2" color="warning" />
            Cara Menamai Produk agar Mudah Dikenali AI
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              AI mencocokkan ucapan dengan <strong>nama varian produk</strong> Anda secara
              keseluruhan — mirip mencocokkan dua kalimat berdasarkan seberapa mirip
              keseluruhan hurufnya, bukan mencari kata kunci satu-satu. Analoginya: mencari
              satu kalimat pendek di antara tumpukan kertas jauh lebih cepat dan akurat
              dibanding mencari satu paragraf panjang yang harus cocok kata demi kata.
              Semakin panjang & rumit nama produk, semakin besar peluang satu kata saja
              salah dengar membuat <strong>seluruh</strong> pencocokan gagal.
            </p>

            <VTable density="compact" class="mb-4">
              <thead>
                <tr>
                  <th>Contoh Nama Varian</th>
                  <th>Keterangan</th>
                  <th style="width: 100px">Rekomendasi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="n in namaProdukContoh" :key="n.nama">
                  <td class="text-body-2 font-weight-medium">{{ n.nama }}</td>
                  <td class="text-body-2">{{ n.ket }}</td>
                  <td>
                    <VChip
                      size="small"
                      :color="n.baik ? 'success' : 'warning'"
                      variant="tonal"
                    >
                      {{ n.baik ? 'Disarankan' : 'Hindari' }}
                    </VChip>
                  </td>
                </tr>
              </tbody>
            </VTable>

            <VAlert type="success" variant="tonal" class="mb-0">
              <strong>💡 Tips praktis:</strong> Buka menu <strong>Produk</strong>, cek nama
              setiap VARIAN (bukan cuma nama produk induknya). Kalau sebuah produk hanya
              punya satu varian, jangan beri nama generik seperti "Reguler"/"Default" —
              pastikan nama variannya sendiri sudah jelas (mis. varian bernama "Americano",
              bukan "Reguler"). Untuk rasa/tambahan khusus seperti "Gula Aren", pertimbangkan
              membuatnya sebagai varian tersendiri dengan nama yang mengandung kata itu
              secara eksplisit, supaya ucapan kasir persis cocok dengan yang tertulis di sistem.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Anatomi Dialog Konfirmasi -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-layout-line" class="me-2" color="info" />
            Mengenal Dialog "Konfirmasi Pesanan Suara"
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-chat-quote-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Transkrip</strong> — teks mentah hasil ucapan Anda, ditampilkan di
                  bagian atas dialog sebagai referensi.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-checkbox-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Checkbox per baris</strong> — centang/hilangkan centang untuk
                  memilih item mana saja yang ikut dimasukkan ke keranjang.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-numbers-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Kolom jumlah</strong> — bisa diedit langsung kalau AI salah hitung qty.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-search-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Kolom produk (autocomplete)</strong> — ketik untuk cari & ganti
                  produk manual, berguna untuk item yang tidak/salah terdeteksi.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-percent-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Badge persentase</strong> — tingkat keyakinan AI:
                  <VChip size="x-small" color="success" variant="tonal" class="mx-1">≥ 85% (hijau)</VChip>
                  cukup yakin,
                  <VChip size="x-small" color="warning" variant="tonal" class="mx-1">60-85% (kuning)</VChip>
                  sebaiknya dicek ulang.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-delete-bin-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Ikon hapus (✕)</strong> — buang baris yang sama sekali tidak diinginkan.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12" md="6">
        <VCard color="success" variant="tonal" class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips untuk Hasil Lebih Akurat
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Ucapkan singkat & langsung.</strong> Format "[jumlah] [nama produk]"
                  seperti "dua americano" jauh lebih akurat daripada kalimat panjang berbelit.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Bicara jelas, jangan terburu-buru.</strong> Jarak mic ke mulut wajar,
                  hindari bicara sambil menoleh menjauh dari perangkat.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Catatan umum boleh diucapkan langsung</strong> — "tanpa gula", "panas",
                  "dingin", "extra shot" otomatis terdeteksi sebagai catatan, bukan nama produk.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Selalu periksa dialog konfirmasi</strong> sebelum klik tambahkan —
                  jangan asal klik walau sudah terbiasa.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Warning -->
      <VCol cols="12" md="6">
        <VCard color="error" variant="tonal" class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-error-warning-line" class="me-2" />
            ⚠️ Hal-hal yang Perlu Dihindari
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>JANGAN gunakan di tempat sangat bising</strong> — musik keras atau
                  suara ramai bisa menurunkan akurasi transkrip.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>JANGAN ucapkan kalimat panjang & tidak spesifik</strong> — sebutkan
                  nama produk secara langsung, bukan deskripsi tidak langsung.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>JANGAN langsung klik "Tambahkan ke Keranjang"</strong> tanpa membaca
                  isi dialog — AI bisa salah dengar, terutama untuk nama produk yang panjang.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>JANGAN beri nama varian generik</strong> ("Reguler", "Default")
                  sebagai satu-satunya identitas produk — AI tidak akan bisa mengenalinya.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Halaman Terkait -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'POS Kasir', sub: 'Panduan lengkap kasir dan transaksi', icon: 'ri-shopping-cart-line', color: 'success', to: '/userguide/operasional/pos-kasir' },
              { title: 'Produk & Katalog', sub: 'Kelola nama produk dan varian agar mudah dikenali AI', icon: 'ri-cup-line', color: 'info', to: '/userguide/produk-inventory/produk' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
